import { createContext, FunctionComponent, PropsWithChildren, useContext, useState, useEffect } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";

const idCompress = (input: any) => input;
const idDecompress = (input: any) => input;
export type UrlParams = {
  productConfiguration?: string;
  subProductConfiguration?: string;
  // Also support route params from main routes
  configId?: string;
  subConfigId?: string;
};

const buildUrl = ({ productConfiguration }: UrlParams) => {
  if (!productConfiguration) {
    return "/productConfigs";
  }
  return `/productConfigs/${productConfiguration}`;
};
export const ProductConfigurationVariablesProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const params = useParams<UrlParams>();
  const { hash } = useLocation();
  const nav = useNavigate();

  // Use local state for tab to ensure re-renders
  const [tab, setTabState] = useState(hash.replace(/^#/, ""));

  // Sync with URL hash on mount and hash changes
  useEffect(() => {
    setTabState(hash.replace(/^#/, ""));
  }, [hash]);

  const setProductConfiguration = (productConfigurationId?: string) => {
    nav(
      buildUrl({
        ...params,
        productConfiguration: idCompress(productConfigurationId),
      })
    );
  };

  const setTab = (tabName: string | undefined) => {
    const newHash = tabName || "";
    // Update state immediately for re-render
    setTabState(newHash);
    // Also update URL hash for bookmarking/sharing
    if (newHash) {
      window.location.hash = newHash;
    } else {
      // Remove hash from URL
      history.pushState(null, "", window.location.pathname + window.location.search);
    }
  };

  // Support both param names: productConfiguration (from module routes) or configId (from main routes)
  const productConfiguration = params.productConfiguration || params.configId;
  const subProductConfiguration = params.subProductConfiguration || params.subConfigId;

  // The active/current configuration being viewed (subproduct takes priority)
  const activeConfigurationId = subProductConfiguration || productConfiguration;

  return (
    <ProductConfigurationVariablesContext.Provider
      value={{
        ...params,
        tab,
        productConfiguration: idDecompress(productConfiguration),
        subProductConfiguration: idDecompress(subProductConfiguration),
        activeConfigurationId: idDecompress(activeConfigurationId),
        setProductConfiguration,
        setTab,
      }}
    >
      {children}
    </ProductConfigurationVariablesContext.Provider>
  );
};

export type ProductConfigurationVariablesContext = {
  productConfiguration?: string;
  subProductConfiguration?: string;
  activeConfigurationId?: string;
  setProductConfiguration: (p?: string) => any;
  tab?: string;
  setTab: (p?: string) => any;
} & Record<string, any>;
export const ProductConfigurationVariablesContext =
  createContext<ProductConfigurationVariablesContext>({
    productConfiguration: "",
    subProductConfiguration: "",
    setProductConfiguration: () => {},
    setSubProductConfiguration: () => {},
    setTab: () => {},
  });

export const useProductConfigurationVariables = () => {
  return useContext(ProductConfigurationVariablesContext);
};

export const ExtendedProductConfigurationVariablesProvider: FunctionComponent<
  PropsWithChildren<Partial<ProductConfigurationVariablesContext>>
> = ({ children, ...props }) => {
  const currentContext = useProductConfigurationVariables();
  const context = { ...currentContext, ...props };
  return (
    <ProductConfigurationVariablesContext.Provider value={context}>
      {children}
    </ProductConfigurationVariablesContext.Provider>
  );
};
